import React, { useEffect, useState } from "react";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import RangeModal from "./Components/RangeModal";
import ProductModal from "./Components/ProductModal";
import api from "../../api";
import "../../styles/Login.scss";

interface Range {
    id: number;
    name: string;
    active: boolean;
    display_order: number;
    created_at?: string;
    updated_at?: string;
}

interface ProductSize {
    id: number;
    product_id: number;
    title: string;
    capacity: string;
    pack_shot: string | null;
    active: boolean;
    display_order: number;
    created_at?: string;
    updated_at?: string;
}

interface Product {
    id: number;
    range_id: number;
    name: string;
    description: string;
    color_code: string;
    active: boolean;
    display_order: number;
    sizes: ProductSize[];
    range?: Range;
    created_at?: string;
    updated_at?: string;
    sizes_count: number;
    about: string | null;
    ingredients: string | null;
}

const AdminProducts = () => {
    const [ranges, setRanges] = useState<Range[]>([]);
    const [products, setProducts] = useState<Product[]>([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [rangeModalOpen, setRangeModalOpen] =
        useState(false);

    const [selectedRange, setSelectedRange] =
        useState<Range | null>(null);

    const [productModalOpen, setProductModalOpen] =
        useState(false);

    const [selectedProduct, setSelectedProduct] =
        useState<Product | null>(null);

    const [deletingRangeId, setDeletingRangeId] =
        useState<number | null>(null);

    const [deletingProductId, setDeletingProductId] =
        useState<number | null>(null);

    const [movingRangeId, setMovingRangeId] =
        useState<number | null>(null);

    const [movingProductId, setMovingProductId] =
        useState<number | null>(null);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError("");

            const [
                rangesResponse,
                productsResponse,
            ] = await Promise.all([
                api.get("/admin/ranges"),
                api.get("/admin/products"),
            ]);

            const rangesData =
                rangesResponse.data?.data ??
                rangesResponse.data ??
                [];

            const productsData =
                productsResponse.data?.data ??
                productsResponse.data ??
                [];

            setRanges(
                [...rangesData].sort(
                    (a: Range, b: Range) =>
                        a.display_order - b.display_order
                )
            );

            setProducts(
                [...productsData].sort(
                    (a: Product, b: Product) =>
                        a.display_order - b.display_order
                )
            );
        } catch (err: any) {
            console.error(
                "Failed to load products:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to load products. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    /*
     * ---------------------------------------------------------
     * RANGES
     * ---------------------------------------------------------
     */

    const handleAddRange = () => {
        setSelectedRange(null);
        setRangeModalOpen(true);
        setError("");
        setSuccess("");
    };

    const handleEditRange = (range: Range) => {
        setSelectedRange(range);
        setRangeModalOpen(true);
        setError("");
        setSuccess("");
    };

    const handleCloseRangeModal = () => {
        setRangeModalOpen(false);
        setSelectedRange(null);
    };

    const handleRangeSaved = async () => {
        handleCloseRangeModal();
        await fetchProducts();
        setSuccess(
            selectedRange
                ? "Range updated successfully."
                : "Range added successfully."
        );
    };

    const handleDeleteRange = async (
        range: Range
    ) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${range.name}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingRangeId(range.id);
            setError("");
            setSuccess("");

            await api.delete(
                `/admin/ranges/${range.id}`
            );

            await fetchProducts();

            setSuccess(
                "Range deleted successfully."
            );
        } catch (err: any) {
            console.error(
                "Failed to delete range:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to delete range. Please try again."
            );
        } finally {
            setDeletingRangeId(null);
        }
    };

    const handleMoveRange = async (
        range: Range,
        direction: "up" | "down"
    ) => {
        try {
            setMovingRangeId(range.id);
            setError("");
            setSuccess("");

            await api.post(
                `/admin/ranges/${range.id}/move`,
                {
                    direction,
                }
            );

            await fetchProducts();
        } catch (err: any) {
            console.error(
                "Failed to move range:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to change range order. Please try again."
            );
        } finally {
            setMovingRangeId(null);
        }
    };

    /*
     * ---------------------------------------------------------
     * PRODUCTS
     * ---------------------------------------------------------
     */

    const handleAddProduct = () => {
        setSelectedProduct(null);
        setProductModalOpen(true);
        setError("");
        setSuccess("");
    };

    const handleEditProduct = (
        product: Product
    ) => {
        setSelectedProduct(product);
        setProductModalOpen(true);
        setError("");
        setSuccess("");
    };

    const handleCloseProductModal = () => {
        setProductModalOpen(false);
        setSelectedProduct(null);
    };

    const handleProductSaved = async () => {
        await fetchProducts();
        setSuccess(
            selectedProduct
                ? "Product updated successfully."
                : "Product added successfully."
        );
    };

    const handleDeleteProduct = async (
        product: Product
    ) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${product.name}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingProductId(product.id);
            setError("");
            setSuccess("");

            await api.delete(
                `/admin/products/${product.id}`
            );

            await fetchProducts();

            setSuccess(
                "Product deleted successfully."
            );
        } catch (err: any) {
            console.error(
                "Failed to delete product:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to delete product. Please try again."
            );
        } finally {
            setDeletingProductId(null);
        }
    };

    const handleMoveProduct = async (
        product: Product,
        direction: "up" | "down"
    ) => {
        try {
            setMovingProductId(product.id);
            setError("");
            setSuccess("");

            await api.post(
                `/admin/products/${product.id}/move`,
                {
                    direction,
                }
            );

            await fetchProducts();
        } catch (err: any) {
            console.error(
                "Failed to move product:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to change product order. Please try again."
            );
        } finally {
            setMovingProductId(null);
        }
    };

    /*
     * ---------------------------------------------------------
     * HELPERS
     * ---------------------------------------------------------
     */

    const getRangeName = (
        rangeId: number
    ) => {
        const range = ranges.find(
            (item) => item.id === rangeId
        );

        return range?.name || "—";
    };

    const sortedRanges = [...ranges].sort(
        (a, b) =>
            a.display_order - b.display_order
    );

    const sortedProducts = [...products].sort(
        (a, b) => {
            const rangeA = ranges.find(
                (range) => range.id === a.range_id
            );

            const rangeB = ranges.find(
                (range) => range.id === b.range_id
            );

            const rangeOrderA =
                rangeA?.display_order ?? rangeA?.id ?? 0;

            const rangeOrderB =
                rangeB?.display_order ?? rangeB?.id ?? 0;

            if (rangeOrderA !== rangeOrderB) {
                return rangeOrderA - rangeOrderB;
            }

            if (a.display_order !== b.display_order) {
                return a.display_order - b.display_order;
            }

            return a.id - b.id;
        }
    );

    const getProductsForRange = (
        rangeId: number
    ) => {
        return sortedProducts.filter(
            (product) =>
                product.range_id === rangeId
        );
    };

    const canMoveProduct = (
        product: Product,
        direction: "up" | "down"
    ) => {
        const rangeProducts =
            getProductsForRange(product.range_id);

        const index = rangeProducts.findIndex(
            (item) => item.id === product.id
        );

        if (direction === "up") {
            return index > 0;
        }

        return (
            index >= 0 &&
            index < rangeProducts.length - 1
        );
    };

    /*
     * ---------------------------------------------------------
     * RENDER
     * ---------------------------------------------------------
     */

    return (
        <div className="admin-page">
            <AdminHeader />

            <div className="admin-body">
                <AdminSidebar />

                <main className="admin-content">
                    <div className="admin-users">

                        <div className="admin-page-header">
                            <div>
                                <h1>
                                    Products
                                </h1>

                                <p>
                                    Manage product
                                    ranges and
                                    products.
                                </p>
                            </div>
                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        {loading ? (
                            <p>
                                Loading products...
                            </p>
                        ) : (
                            <>
                                {/* RANGES */}

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>
                                                Ranges
                                            </h2>

                                            <p>
                                                Manage the
                                                product
                                                ranges.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={
                                                handleAddRange
                                            }
                                        >
                                            Add range
                                        </button>
                                    </div>

                                    {sortedRanges.length ===
                                    0 ? (
                                        <p>
                                            No ranges
                                            added yet.
                                        </p>
                                    ) : (
                                        <div className="admin-users-table">
                                            <table>
                                                <thead>
                                                <tr>
                                                    <th>
                                                        Range
                                                    </th>

                                                    <th>
                                                        Products
                                                    </th>

                                                    <th>
                                                        Active
                                                    </th>

                                                    <th>
                                                        Actions
                                                    </th>
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {sortedRanges.map(
                                                    (
                                                        range,
                                                        index
                                                    ) => {
                                                        const rangeProducts =
                                                            getProductsForRange(
                                                                range.id
                                                            );

                                                        return (
                                                            <tr
                                                                key={
                                                                    range.id
                                                                }
                                                            >
                                                                <td>
                                                                    {
                                                                        range.name
                                                                    }
                                                                </td>

                                                                <td>
                                                                    {
                                                                        rangeProducts.length
                                                                    }
                                                                </td>

                                                                <td>
                                                                    <span
                                                                        className={`admin-role ${
                                                                            range.active
                                                                                ? "admin-role-admin"
                                                                                : "admin-role-editor"
                                                                        }`}
                                                                    >
                                                            {range.active
                                                                ? "Active"
                                                                : "Inactive"}
                                                        </span>
                                                                </td>

                                                                <td>
                                                                    <div className="admin-user-actions">
                                                                        <button
                                                                            type="button"
                                                                            className="admin-table-button"
                                                                            onClick={() =>
                                                                                handleMoveRange(
                                                                                    range,
                                                                                    "up"
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                index ===
                                                                                0 ||
                                                                                movingRangeId !==
                                                                                null ||
                                                                                deletingRangeId !==
                                                                                null
                                                                            }
                                                                            aria-label={`Move ${range.name} up`}
                                                                        >
                                                                            ↑
                                                                        </button>

                                                                        <button
                                                                            type="button"
                                                                            className="admin-table-button"
                                                                            onClick={() =>
                                                                                handleMoveRange(
                                                                                    range,
                                                                                    "down"
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                index ===
                                                                                sortedRanges.length -
                                                                                1 ||
                                                                                movingRangeId !==
                                                                                null ||
                                                                                deletingRangeId !==
                                                                                null
                                                                            }
                                                                            aria-label={`Move ${range.name} down`}
                                                                        >
                                                                            ↓
                                                                        </button>

                                                                        <button
                                                                            type="button"
                                                                            className="admin-table-button"
                                                                            onClick={() =>
                                                                                handleEditRange(
                                                                                    range
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                movingRangeId !==
                                                                                null ||
                                                                                deletingRangeId !==
                                                                                null
                                                                            }
                                                                        >
                                                                            Edit
                                                                        </button>

                                                                        <button
                                                                            type="button"
                                                                            className="admin-table-button admin-table-button-danger"
                                                                            onClick={() =>
                                                                                handleDeleteRange(
                                                                                    range
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                deletingRangeId ===
                                                                                range.id ||
                                                                                movingRangeId !==
                                                                                null
                                                                            }
                                                                        >
                                                                            {deletingRangeId ===
                                                                            range.id
                                                                                ? "Deleting..."
                                                                                : "Delete"}
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>

                                {/* PRODUCTS */}

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>
                                                Products
                                            </h2>

                                            <p>
                                                Manage the
                                                products
                                                within each
                                                range.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={
                                                handleAddProduct
                                            }
                                            disabled={
                                                ranges.length ===
                                                0
                                            }
                                        >
                                            Add product
                                        </button>
                                    </div>

                                    {ranges.length ===
                                        0 && (
                                            <p>
                                                Add a range
                                                before
                                                creating a
                                                product.
                                            </p>
                                        )}

                                    {ranges.length >
                                        0 &&
                                        products.length ===
                                        0 && (
                                            <p>
                                                No products
                                                added yet.
                                            </p>
                                        )}

                                    {products.length >
                                        0 && (
                                            <div className="admin-users-table">
                                                <table>
                                                    <thead>
                                                    <tr>
                                                        <th>
                                                            Name
                                                        </th>

                                                        <th>
                                                            Range
                                                        </th>

                                                        <th>
                                                            Colour
                                                        </th>

                                                        <th>
                                                            Sizes
                                                        </th>

                                                        <th>
                                                            Active
                                                        </th>

                                                        <th>
                                                            Actions
                                                        </th>
                                                    </tr>
                                                    </thead>

                                                    <tbody>
                                                    {sortedProducts.map(
                                                        (
                                                            product
                                                        ) => {
                                                            const rangeProducts =
                                                                getProductsForRange(
                                                                    product.range_id
                                                                );

                                                            const productIndex =
                                                                rangeProducts.findIndex(
                                                                    (
                                                                        item
                                                                    ) =>
                                                                        item.id ===
                                                                        product.id
                                                                );

                                                            const canMoveUp =
                                                                productIndex >
                                                                0;

                                                            const canMoveDown =
                                                                productIndex >=
                                                                0 &&
                                                                productIndex <
                                                                rangeProducts.length -
                                                                1;

                                                            return (
                                                                <tr
                                                                    key={
                                                                        product.id
                                                                    }
                                                                >
                                                                    <td>
                                                                        {
                                                                            product.name
                                                                        }
                                                                    </td>

                                                                    <td>
                                                                        {product.range?.name ||
                                                                            getRangeName(
                                                                                product.range_id
                                                                            )}
                                                                    </td>

                                                                    <td>
                                                                        <div
                                                                            style={{
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                gap:
                                                                                    "8px",
                                                                            }}
                                                                        >
                                                                            <span
                                                                                style={{
                                                                                    width:
                                                                                        "24px",
                                                                                    height:
                                                                                        "24px",
                                                                                    borderRadius:
                                                                                        "4px",
                                                                                    backgroundColor:
                                                                                        product.color_code ||
                                                                                        "transparent",
                                                                                    border:
                                                                                        "1px solid rgba(0,0,0,0.15)",
                                                                                    display:
                                                                                        "inline-block",
                                                                                }}
                                                                            />

                                                                            {
                                                                                product.color_code
                                                                            }
                                                                        </div>
                                                                    </td>

                                                                    <td>
                                                                        {
                                                                            (product.sizes_count)
                                                                        }
                                                                    </td>

                                                                    <td>
                                                                        <span
                                                                            className={`admin-role ${
                                                                                product.active
                                                                                    ? "admin-role-admin"
                                                                                    : "admin-role-editor"
                                                                            }`}
                                                                        >
                                                            {product.active
                                                                ? "Active"
                                                                : "Inactive"}
                                                        </span>
                                                                    </td>

                                                                    <td>
                                                                        <div className="admin-user-actions">
                                                                            <button
                                                                                type="button"
                                                                                className="admin-table-button"
                                                                                onClick={() =>
                                                                                    handleMoveProduct(
                                                                                        product,
                                                                                        "up"
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    !canMoveUp ||
                                                                                    movingProductId !==
                                                                                    null ||
                                                                                    deletingProductId !==
                                                                                    null
                                                                                }
                                                                                aria-label={`Move ${product.name} up`}
                                                                            >
                                                                                ↑
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                className="admin-table-button"
                                                                                onClick={() =>
                                                                                    handleMoveProduct(
                                                                                        product,
                                                                                        "down"
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    !canMoveDown ||
                                                                                    movingProductId !==
                                                                                    null ||
                                                                                    deletingProductId !==
                                                                                    null
                                                                                }
                                                                                aria-label={`Move ${product.name} down`}
                                                                            >
                                                                                ↓
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                className="admin-table-button"
                                                                                onClick={() =>
                                                                                    handleEditProduct(
                                                                                        product
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    movingProductId !==
                                                                                    null ||
                                                                                    deletingProductId !==
                                                                                    null
                                                                                }
                                                                            >
                                                                                Edit
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                className="admin-table-button admin-table-button-danger"
                                                                                onClick={() =>
                                                                                    handleDeleteProduct(
                                                                                        product
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    deletingProductId ===
                                                                                    product.id ||
                                                                                    movingProductId !==
                                                                                    null
                                                                                }
                                                                            >
                                                                                {deletingProductId ===
                                                                                product.id
                                                                                    ? "Deleting..."
                                                                                    : "Delete"}
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                    )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                </section>
                            </>
                        )}
                    </div>
                </main>
            </div>

            {/* RANGE MODAL */}

            {rangeModalOpen && (
                <RangeModal
                    range={selectedRange}
                    onClose={
                        handleCloseRangeModal
                    }
                    onSaved={
                        handleRangeSaved
                    }
                />
            )}

            {/* PRODUCT MODAL */}

            {productModalOpen && (
                <ProductModal
                    product={
                        selectedProduct
                    }
                    ranges={ranges}
                    onClose={
                        handleCloseProductModal
                    }
                    onSaved={
                        handleProductSaved
                    }
                />
            )}
        </div>
    );
};

export default AdminProducts;
